import React, { useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';
import { useThemeStore } from '@/stores/themeStore';
import { useAgentStore } from '@/stores/agentStore';
import { useConsole } from '@/console/ConsoleContext';
import { SEARCH_MAX_CHARS } from '@/console/folders';
import { useSearchNavigation } from '@/console/useSearchNavigation';
import { AVAIL_OPTIONS, availDotClass, availLabelKey } from '@/console/availability';
import type { AgentAvailability } from '@/types';

/** Top bar: search, live hub status, availability, language, theme, notifications. */
export const Topbar: React.FC = () => {
  const { t, hubStatusLabel } = useT();
  const lang = useLangStore((s) => s.lang);
  const setLang = useLangStore((s) => s.setLang);
  const mode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const myAvailability = useAgentStore((s) => s.myAvailability);
  // The search box asks the SERVER (GET /api/sessions?q=): 300 ms after the last keystroke, from two characters, as a
  // folder-shaped view in the URL (useSearchNavigation). The text lives in the conversation store, which forgets it with
  // everything else when the signed-in agent changes.
  const search = useSearchNavigation();
  const { hub } = useConsole();

  const [availOpen, setAvailOpen] = useState(false);

  const connected = hub.status === 'connected';
  // "Live" when connected (design wording); otherwise the localized hub-status
  // label (Connecting… / Reconnecting… / Offline) from the shared enum mapper.
  const statusLabel = connected ? t('liveConnected') : hubStatusLabel(hub.status);

  const chooseAvailability = async (a: AgentAvailability) => {
    setAvailOpen(false);
    try {
      await hub.setAvailability(a);
    } catch {
      // Hub not connected — availability can't be pushed right now. The dot
      // reflects the last confirmed state; the agent can retry once "Live".
    }
  };

  return (
    <header className="ac-topbar">
      <div className="ac-search">
        <Icon name="search" size={17} />
        <input
          placeholder={t('search_placeholder')}
          aria-label={t('search_placeholder')}
          value={search.text}
          maxLength={SEARCH_MAX_CHARS}
          autoComplete="off"
          onChange={(e) => search.setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              search.submit();
            } else if (e.key === 'Escape' && search.text !== '') {
              e.preventDefault();
              search.clear();
            }
          }}
          data-testid="topbar-search"
        />
        {search.text !== '' && (
          <button
            type="button"
            className="ac-search-clear"
            onClick={search.clear}
            aria-label={t('cl_search_clear')}
            data-testid="topbar-search-clear"
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      <div className="ac-topbar-spacer" />

      {/* Live hub status */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 12.5,
          fontWeight: 600,
          color: 'var(--text-muted)',
          marginInlineEnd: 4,
        }}
        data-testid="hub-status"
        data-status={hub.status}
      >
        <span
          className={'ac-status-dot ' + (connected ? 'online ac-pulse' : 'away')}
          style={{ width: 8, height: 8, borderRadius: '50%', color: 'var(--success)' }}
        />
        {statusLabel}
      </div>

      {/* Availability */}
      <div style={{ position: 'relative' }}>
        <button
          className="ac-avail"
          onClick={() => setAvailOpen((o) => !o)}
          data-testid="availability-toggle"
          aria-haspopup="listbox"
          aria-expanded={availOpen}
        >
          <span className={'ac-status-dot ' + availDotClass(myAvailability)} />
          {t(availLabelKey(myAvailability))}
          <Icon name="chevron" size={13} style={{ transform: 'rotate(90deg)', opacity: 0.6 }} />
        </button>
        {availOpen && (
          <div
            className="ac-card ac-fade-up"
            role="listbox"
            style={{
              position: 'absolute',
              top: 46,
              insetInlineEnd: 0,
              width: 160,
              padding: 6,
              zIndex: 30,
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            {AVAIL_OPTIONS.map((o) => (
              <button
                key={o}
                className="ac-nav-item"
                style={{ color: 'var(--text)' }}
                onClick={() => chooseAvailability(o)}
                data-testid={`availability-option-${o}`}
                role="option"
                aria-selected={myAvailability === o}
              >
                <span
                  className={'ac-status-dot ' + availDotClass(o)}
                  style={{ width: 9, height: 9, borderRadius: '50%' }}
                />
                {t(availLabelKey(o))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Language toggle */}
      <div className="ac-seg" role="group" aria-label="Language">
        <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')} data-testid="lang-en">
          EN
        </button>
        <button className={lang === 'he' ? 'active' : ''} onClick={() => setLang('he')} data-testid="lang-he">
          עב
        </button>
      </div>

      {/* Theme toggle */}
      <button
        className="ac-icon-btn"
        onClick={toggleTheme}
        aria-label={t('toggleThemeAria')}
        data-testid="theme-toggle"
      >
        <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={18} />
      </button>

      {/* Notifications */}
      <button className="ac-icon-btn" aria-label={t('notificationsAria')} data-testid="notifications">
        <Icon name="bell" size={18} />
        <span className="ac-dot-badge" />
      </button>
    </header>
  );
};
